export const getInitials = (name, lastname) => {
  return `${(name || "")[0] || ""}${(lastname || "")[0] || ""}`.toUpperCase();
};

export const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 55%, 65%)`;
};

export const styles = {
  container: {
    width: "100%",
    height: "calc(100vh - 100px)",
    display: "flex",
    flexDirection: "column",
    p: { xs: 1.5, sm: 2, md: 3 },
    boxSizing: "border-box",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    gap: 1.5,
    p: { xs: 1.5, sm: 2 },
    mb: 2,
    borderRadius: 3,
    bgcolor: "rgba(208, 116, 155, 0.04)",
    border: "1px solid rgba(208, 116, 155, 0.12)",
    flexShrink: 0,
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flexWrap: "wrap",
  },

  liveDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    bgcolor: "#f44336",
    animation: "pulse 1.5s ease-in-out infinite",
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1, transform: "scale(1)" },
      "50%": { opacity: 0.5, transform: "scale(1.3)" },
    },
  },

  connectedChip: (theme) => ({
    borderColor: "rgba(208, 116, 155, 0.4)",
    color: theme.palette.primary.main,
    fontWeight: 500,
  }),

  chatToggle: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
  },

  notifyButton: (theme) => ({
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    px: 3,
    py: 1,
    bgcolor: theme.palette.primary.main,
    "&:hover": { bgcolor: theme.palette.primary.dark },
    minWidth: { xs: "100%", sm: "auto" },
  }),

  content: {
    display: "flex",
    flex: 1,
    gap: 2,
    overflow: "hidden",
    minHeight: 0,
  },

  commentsPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    overflow: "hidden",
    minWidth: 0,
  },

  commentsHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 2,
    py: 1.5,
    borderBottom: "1px solid",
    borderColor: "divider",
    bgcolor: "rgba(0,0,0,0.01)",
    flexShrink: 0,
  },

  commentCountChip: {
    height: 20,
    fontSize: "0.7rem",
    bgcolor: "rgba(0,0,0,0.06)",
  },

  commentsList: {
    flex: 1,
    overflowY: "auto",
    p: 1,
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      bgcolor: "rgba(0,0,0,0.1)",
      borderRadius: 3,
      "&:hover": { bgcolor: "rgba(0,0,0,0.2)" },
    },
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    opacity: 0.4,
    gap: 1,
  },

  commentItem: (index) => ({
    py: 0.75,
    px: 1.5,
    borderRadius: 2,
    bgcolor: index % 2 === 0 ? "transparent" : "rgba(208, 116, 155, 0.03)",
    alignItems: "flex-start",
    transition: "background-color 0.3s",
    animation: "fadeIn 0.3s ease-in",
    "@keyframes fadeIn": {
      from: { opacity: 0, transform: "translateY(8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  }),

  commentAvatar: (name) => ({
    width: 30,
    height: 30,
    fontSize: "0.7rem",
    fontWeight: 600,
    bgcolor: stringToColor(name),
  }),

  usersPanel: (isTablet) => ({
    width: isTablet ? 200 : 260,
    display: "flex",
    flexDirection: "column",
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    overflow: "hidden",
    p: 2,
    flexShrink: 0,
  }),

  usersPanelContainer: (isMobile) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    ...(isMobile && { width: 280, p: 2 }),
  }),

  usersPanelHeader: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
    px: isMobile ? 0 : 1,
  }),

  usersPanelChip: (theme) => ({
    bgcolor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.75rem",
    height: 24,
  }),

  usersList: {
    flex: 1,
    overflowY: "auto",
    p: 0,
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": {
      bgcolor: "rgba(208, 116, 155, 0.3)",
      borderRadius: 2,
    },
  },

  userItem: {
    py: 0.75,
    px: 1,
    borderRadius: 2,
    mb: 0.5,
    transition: "background-color 0.2s",
    "&:hover": { bgcolor: "rgba(208, 116, 155, 0.06)" },
  },

  onlineBadge: {
    fontSize: 10,
    color: "#4caf50",
    filter: "drop-shadow(0 0 2px rgba(76, 175, 80, 0.6))",
  },

  userAvatar: (name, lastname) => ({
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    fontWeight: 600,
    bgcolor: stringToColor(`${name}${lastname}`),
  }),

  fab: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 10,
  },

  successAlert: {
    position: "fixed",
    top: 80,
    right: 24,
    zIndex: 1300,
    minWidth: 280,
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
};
